/**
 * legacyHtml — render the small HTML subset the legacy SWF emitted in
 * chat bodies (especially `generic_mess` frames). Safe by construction:
 * we tokenise into an allow-list of tags and build React nodes, NEVER
 * `dangerouslySetInnerHTML`.
 *
 * Supported (matches strings-all.txt grep of the SWF):
 *   <b>…</b>           bold
 *   <i>…</i>           italic
 *   <u>…</u>           underline
 *   <s>…</s>           strike (used by legacy banned-name decoration)
 *   <font color="…">…</font>             — single attribute, hex or named
 *   <font color="…" size="…">…</font>    — size in px, clamped 8–24
 *
 * Anything outside the allow-list (incl. <script>, <iframe>, <a>, etc.)
 * is rendered as plain text — angle brackets included.
 */
import type { ReactNode, CSSProperties } from 'react';

interface TagNode { kind: 'tag'; name: string; attrs: Record<string, string>; children: ASTNode[]; }
interface TextNode { kind: 'text'; text: string; }
type ASTNode = TagNode | TextNode;

const ALLOWED_TAGS = new Set(['b', 'i', 'u', 's', 'font']);
const ALLOWED_FONT_ATTRS = new Set(['color', 'size']);

// Match <tag attr='val' attr2="val">   or  </tag>  ; case-insensitive.
const TAG_RE = /<\s*(\/?)\s*([a-zA-Z]+)([^>]*)>/g;
const ATTR_RE = /([a-zA-Z]+)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/g;

function parseAttrs(s: string): Record<string, string> {
    const out: Record<string, string> = {};
    ATTR_RE.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = ATTR_RE.exec(s)) !== null) {
        const k = m[1]!.toLowerCase();
        const v = m[2] ?? m[3] ?? m[4] ?? '';
        out[k] = v;
    }
    return out;
}

function parse(s: string): ASTNode[] {
    const root: ASTNode[] = [];
    const stack: ASTNode[][] = [root];
    const openTags: string[] = [];
    let last = 0;
    TAG_RE.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = TAG_RE.exec(s)) !== null) {
        if (m.index > last) {
            stack[stack.length - 1]!.push({ kind: 'text', text: s.slice(last, m.index) });
        }
        const isClose = m[1] === '/';
        const name = m[2]!.toLowerCase();
        if (!ALLOWED_TAGS.has(name)) {
            // Treat as literal text
            stack[stack.length - 1]!.push({ kind: 'text', text: m[0] });
        } else if (isClose) {
            // Pop until we find the matching open. If not found, treat as text.
            const depth = openTags.lastIndexOf(name);
            if (depth < 0) {
                stack[stack.length - 1]!.push({ kind: 'text', text: m[0] });
            } else {
                // Close all unclosed inner tags too
                while (openTags.length - 1 > depth) {
                    openTags.pop();
                    stack.pop();
                }
                openTags.pop();
                stack.pop();
            }
        } else {
            const attrs = parseAttrs(m[3] ?? '');
            const node: TagNode = { kind: 'tag', name, attrs, children: [] };
            stack[stack.length - 1]!.push(node);
            stack.push(node.children);
            openTags.push(name);
        }
        last = m.index + m[0].length;
    }
    if (last < s.length) {
        stack[stack.length - 1]!.push({ kind: 'text', text: s.slice(last) });
    }
    return root;
}

function safeColor(v: string | undefined): string | undefined {
    if (!v) return undefined;
    const t = v.trim();
    if (/^#[0-9A-Fa-f]{3,8}$/.test(t)) return t;
    // Named colour: simple a-z chars, no spaces
    if (/^[a-z]{2,20}$/i.test(t)) return t;
    return undefined;
}

function safeSize(v: string | undefined): string | undefined {
    if (!v) return undefined;
    const n = parseInt(v, 10);
    if (Number.isFinite(n)) {
        const clamped = Math.max(8, Math.min(24, n));
        return `${clamped}px`;
    }
    return undefined;
}

function render(nodes: ASTNode[], baseKey = 'h'): ReactNode[] {
    return nodes.map((n, i): ReactNode => {
        if (n.kind === 'text') return n.text;
        const k = `${baseKey}-${i}`;
        if (n.name === 'b') return <b key={k}>{render(n.children, k)}</b>;
        if (n.name === 'i') return <i key={k}>{render(n.children, k)}</i>;
        if (n.name === 'u') return <u key={k}>{render(n.children, k)}</u>;
        if (n.name === 's') return <s key={k}>{render(n.children, k)}</s>;
        if (n.name === 'font') {
            const style: CSSProperties = {};
            for (const [attr, val] of Object.entries(n.attrs)) {
                if (!ALLOWED_FONT_ATTRS.has(attr)) continue;
                if (attr === 'color') {
                    const c = safeColor(val);
                    if (c) style.color = c;
                } else if (attr === 'size') {
                    const sz = safeSize(val);
                    if (sz) style.fontSize = sz;
                }
            }
            return <span key={k} style={style}>{render(n.children, k)}</span>;
        }
        return null;
    });
}

/** Returns true if `s` contains any of our allow-list tags. */
export function hasLegacyHtml(s: string): boolean {
    return /<\s*\/?\s*(b|i|u|s|font)\b/i.test(s);
}

/** Render a legacy-HTML chat body safely. */
export function renderLegacyHtml(body: string): ReactNode {
    return render(parse(body));
}
